import React from 'react';
import { ClipboardList } from 'lucide-react';
import { FollowupCard } from './FollowupCard';
import { formatGroupHeader } from './followupHelpers';
import type { AgentGroup, PendingFollowup } from './types';

interface TaskListProps {
  loading: boolean;
  visible: PendingFollowup[];
  totalLoaded: number;
  searchActive: boolean;
  agentName: string | null;
  groups: AgentGroup[] | null;
  onOpenPos: (posId: string) => void;
  onQuickVisit: (followup: PendingFollowup) => void;
  onFullVisit: (followup: PendingFollowup) => void;
}

export function TaskList({
  loading,
  visible,
  totalLoaded,
  searchActive,
  agentName,
  groups,
  onOpenPos,
  onQuickVisit,
  onFullVisit,
}: TaskListProps) {
  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="bg-white p-4 rounded-xl border border-gray-200 animate-pulse h-40"
          />
        ))}
      </div>
    );
  }

  if (visible.length === 0) {
    return (
      <EmptyState
        hasSearchMatches={totalLoaded > 0 && searchActive}
        agentName={agentName}
      />
    );
  }

  if (groups) {
    return (
      <GroupedList
        groups={groups}
        onOpenPos={onOpenPos}
        onQuickVisit={onQuickVisit}
        onFullVisit={onFullVisit}
      />
    );
  }

  return (
    <FlatList
      items={visible}
      onOpenPos={onOpenPos}
      onQuickVisit={onQuickVisit}
      onFullVisit={onFullVisit}
    />
  );
}

function FlatList({
  items,
  onOpenPos,
  onQuickVisit,
  onFullVisit,
}: {
  items: PendingFollowup[];
  onOpenPos: (posId: string) => void;
  onQuickVisit: (followup: PendingFollowup) => void;
  onFullVisit: (followup: PendingFollowup) => void;
}) {
  return (
    <div className="space-y-3">
      {items.map((followup) => (
        <FollowupCard
          key={followup.id}
          followup={followup}
          onOpenPos={onOpenPos}
          onQuickVisit={onQuickVisit}
          onFullVisit={onFullVisit}
        />
      ))}
    </div>
  );
}

function GroupedList({
  groups,
  onOpenPos,
  onQuickVisit,
  onFullVisit,
}: {
  groups: AgentGroup[];
  onOpenPos: (posId: string) => void;
  onQuickVisit: (followup: PendingFollowup) => void;
  onFullVisit: (followup: PendingFollowup) => void;
}) {
  return (
    <div className="space-y-5">
      {groups.map((group) => (
        <section key={group.userId} className="space-y-3">
          <h2 className="text-sm font-bold text-gray-800 px-1">
            {formatGroupHeader(group)}
          </h2>
          {group.tasks.map((followup) => (
            <FollowupCard
              key={followup.id}
              followup={followup}
              onOpenPos={onOpenPos}
              onQuickVisit={onQuickVisit}
              onFullVisit={onFullVisit}
            />
          ))}
        </section>
      ))}
    </div>
  );
}

function EmptyState({
  hasSearchMatches,
  agentName,
}: {
  hasSearchMatches: boolean;
  agentName: string | null;
}) {
  let title = 'No pending to-dos';
  let hint = 'All follow-ups have been completed!';
  if (hasSearchMatches) {
    title = 'No matching PoS';
    hint = 'Try a different search term or clear the search.';
  } else if (agentName) {
    title = 'No pending to-dos';
    hint = `Nothing open for ${agentName} right now.`;
  }

  return (
    <div className="text-center py-12 bg-white rounded-xl border-2 border-dashed border-gray-300 mx-4">
      <ClipboardList size={48} className="mx-auto text-gray-300 mb-3" />
      <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-600">{hint}</p>
    </div>
  );
}
